import React, { useMemo, useState, useEffect } from 'react';
import { collection, query, getDocs, where, limit, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { 
  Users, 
  Plus, 
  MessageSquare,
  Heart,
  Globe,
  Lock,
  Eye
} from 'lucide-react';
import { Group } from '../../types/community';
import { Link } from 'react-router-dom';

export const CommunitySimple: React.FC = () => {
  console.log('🏘️ CommunitySimple component rendered');
  
  const { user } = useAuth();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  
  console.log('🏘️ CommunitySimple state:', { 
    hasUser: !!user, 
    loading, 
    groupsCount: groups.length,
    error
  });

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    fetchGroups();
  }, [user]);

  const fetchGroups = async () => {
    try {
      console.log('🔍 Fetching groups...');
      setLoading(true);
      setError(null);
      
      const groupsQuery = query(
        collection(db, 'groups'),
        where('privacy', '==', 'public'),
        where('isArchived', '==', false),
        orderBy('lastActivity', 'desc'),
        limit(50)
      );
      
      const groupsSnap = await getDocs(groupsQuery);
      console.log('🔍 Groups query result:', groupsSnap.docs.length, 'documents');
      
      const groupsData = groupsSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() ?? data.createdAt ?? null,
          updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt ?? null,
          lastActivity: data.lastActivity?.toDate?.() ?? data.lastActivity ?? null,
        } as Group;
      });
      
      console.log('🔍 Groups loaded:', groupsData.length);
      setGroups(groupsData);
    } catch (error) {
      console.error('❌ Error fetching groups:', error);
      setError('Failed to load groups. Please try again.');
    } finally {
      setLoading(false);
      console.log('✅ CommunitySimple fetch complete');
    }
  };

  const categories = useMemo(() => {
    const set = new Set<string>();
    groups.forEach((g) => g.category && set.add(g.category));
    return ['all', ...Array.from(set)];
  }, [groups]);

  const visibleGroups = useMemo(() => {
    let list = groups;
    if (activeCategory !== 'all') list = list.filter((g) => g.category === activeCategory);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((g) =>
        (g.name || '').toLowerCase().includes(q) ||
        (g.description || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [groups, activeCategory, search]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600 text-sm">Please log in to access the community.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white px-4 py-6">
        <div className="max-w-screen-md mx-auto space-y-4">
          <div className="h-8 w-40 bg-gray-200 rounded animate-pulse" />
          <div className="h-10 w-full bg-gray-200 rounded-xl animate-pulse" />
          <div className="flex gap-2 overflow-x-auto">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-8 w-20 bg-gray-200 rounded-full animate-pulse" />
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-28 bg-gray-200 rounded-2xl animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-gradient-to-br from-red-400 to-red-500 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto shadow-2xl mb-6">
            <span className="text-white text-2xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Error Loading Community</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={fetchGroups}
            className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Sticky header with create CTA */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-gray-200">
        <div className="max-w-screen-md mx-auto px-4 py-3 flex items-center justify-between">
          <div className="text-base font-semibold text-gray-900">Community</div>
          <Link
            to="/community/create-group"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-600 text-white text-sm shadow-sm active:scale-95"
          >
            <Plus className="w-4 h-4" />
            Create
          </Link>
        </div>
      </div>

      <div className="max-w-screen-md mx-auto px-4 py-4 space-y-4">
        {/* Search */}
        <div className="bg-white rounded-2xl border border-gray-200 p-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search groups…"
            className="w-full bg-transparent outline-none text-sm"
          />
        </div>

        {/* Quick stats (compact) */}
        <div className="grid grid-cols-3 gap-2">
            <div className="text-center">
              <div className="bg-white border border-gray-200 rounded-xl p-3">
                <div className="text-xs text-gray-500">Active</div>
                <div className="text-lg font-semibold">{groups.length}</div>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-white border border-gray-200 rounded-xl p-3">
                <div className="text-xs text-gray-500">Your groups</div>
                <div className="text-lg font-semibold">0</div>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-white border border-gray-200 rounded-xl p-3">
                <div className="text-xs text-gray-500">Members</div>
                <div className="text-lg font-semibold">0</div>
              </div>
            </div>
        </div>

        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCategory(c)}
              className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border ${
                activeCategory === c ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200 text-gray-700'
              }`}
            >
              {c.charAt(0).toUpperCase() + c.slice(1)}
            </button>
          ))}
        </div>

        {/* Groups */}
        <div className="space-y-3">
          {visibleGroups.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-6" />
              <h3 className="text-xl font-bold text-gray-800 mb-4">No Groups Yet</h3>
              <p className="text-gray-600 mb-8">
                Be the first to create a support group for your fellow first responders.
              </p>
              <Link
                to="/community/create-group"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-lg"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create First Group
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {visibleGroups.map((group) => {
                const groupUrl = `/community/groups/${group.id}`;
                console.log('🔍 Creating link for group:', group.name, 'URL:', groupUrl);
                return (
                <Link
                  key={group.id}
                  to={groupUrl}
                  onClick={() => console.log('➡️ navigating to group', group.id, 'URL:', groupUrl)}
                  className="bg-white border border-gray-200 rounded-2xl p-4 hover:shadow-lg transition-all duration-200 active:scale-[0.99] cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="bg-blue-500 p-3 rounded-xl shadow">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex items-center space-x-1">
                      {group.privacy === 'private' ? (
                        <Lock className="w-4 h-4 text-gray-400" />
                      ) : (
                        <Globe className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>

                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    {group.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {group.description}
                  </p>

                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <Users className="w-3 h-3" />
                        <span>{group.memberCount || 0} members</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MessageSquare className="w-3 h-3" />
                        <span>{group.postCount || 0} posts</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {group.category}
                      </span>
                      <div className="text-blue-600 font-medium text-sm">
                        Join Group →
                      </div>
                    </div>
                  </div>
                </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Bottom create CTA for mobile */}
        <div className="h-16" />
        <Link
          to="/community/create-group"
          className="fixed bottom-4 right-4 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-blue-600 text-white shadow-lg active:scale-95"
        >
          <Plus className="w-5 h-5" />
          Create group
        </Link>
      </div>
    </div>
  );
};